import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const CyberInfinityAnimation = () => {
  const pathD =
    "M100,100 C100,20 200,20 250,100 C300,180 400,180 400,100 C400,20 300,20 250,100 C200,180 100,180 100,100 Z";

  // Blue tones for Left, Red tones for Right
  const blueTones = ["#12b93cff", "#2c69b4ff", "#ca9f29ff", "#30a0a8ff"];
  const redTones = ["#e74496ff", "#624fb8ff", "#f07713ff", "#8123b8ff"];

  const labelData = [
    { title: "Response & Containment" }, { title: "Recovery & Improvement" },
    { title: "Evasion & Adaptation" }, { title: "Privilege Escalation" },
    { title: "Persistence & Simulation" }, { title: "Assessment & Reporting" },
    { title: "Monitoring & Detection" }, { title: "Analysis & Investigation" }
  ];

  const pathRef = useRef(null);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    if (!pathRef.current) return;
    const length = pathRef.current.getTotalLength();
    const pts = [];
    for (let i = 0; i < 8; i++) {
      const pos = ((i + 0.5) / 8) * length;
      pts.push(pathRef.current.getPointAtLength(pos));
    }
    setPoints(pts);
  }, []);

  return (
    <div className="relative w-full h-[320px] sm:h-[450px] lg:h-[500px] flex items-center justify-center bg-transparent overflow-visible">

      <div className="relative w-full h-full scale-[0.75] xs:scale-[0.75] sm:scale-[0.8] lg:scale-100 transition-transform duration-500 origin-center flex items-center justify-center">
      <svg viewBox="0 0 500 250" className="w-full max-w-4xl overflow-visible">
        <defs>
          {/* NEON GLOW FILTER */}
          <filter id="neon-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* SPLIT COLOR GRADIENT FOR PATH */}
          <linearGradient id="splitPathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="48%" stopColor="#38bdf8" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="52%" stopColor="#fb7185" stopOpacity="0.6" />
          </linearGradient>
        </defs>

        {/* ANIMATED SPLIT-COLOR PATH */}
        <motion.path
          ref={pathRef}
          d={pathD}
          fill="none"
          stroke="url(#splitPathGradient)" 
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="0 10"
          animate={{ strokeDashoffset: [0, -200] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />

        {points.map((p, i) => {
          // Determine color based on X position relative to center
          const isLeftCycle = p.x <= 250.1; 
          const themeColor = isLeftCycle ? blueTones[i % 4] : redTones[i % 4];

          let xOffset = -40; 
          let yOffset = 0;
          let lineX2 = p.x;
          let lineY2 = p.y;

          if (i === 0 || i === 7) {
            xOffset = -130; yOffset = -10; lineX2 = p.x - 50; lineY2 = p.y;
          } 
          else if (i === 3 || i === 4) {
            xOffset = 50; yOffset = -10; lineX2 = p.x + 50; lineY2 = p.y;
          }
          else if (i === 1 || i === 6) {
            xOffset = -70; yOffset = -45; lineX2 = p.x; lineY2 = p.y - 27;
          }
          else if (i === 2 || i === 5) {
            xOffset = -10; yOffset = 25; lineX2 = p.x; lineY2 = p.y + 27;
          }

          return (
            <g key={i}>
              <line 
                x1={p.x} y1={p.y} 
                x2={lineX2} y2={lineY2} 
                stroke={themeColor} strokeWidth="1" strokeDasharray="2 2"
                className="opacity-60"
              />

              <g filter="url(#neon-glow)">
                <circle cx={p.x} cy={p.y} r="4.5" fill={themeColor} opacity="0.4" />
                <circle cx={p.x} cy={p.y} r="2" fill="#ffffff" />
              </g>

              <foreignObject 
                x={p.x + xOffset} y={p.y + yOffset} 
                width="80" height="20"
                className="overflow-visible"
              >
                <div className={`flex w-full h-full items-center ${
                  (i === 0 || i === 7) ? 'justify-end' : 
                  (i === 3 || i === 4) ? 'justify-start' : 'justify-center'
                }`}>
                  <div 
                    className="px-2.5 py-0.5 rounded-full text-white font-bold text-[6.3px] whitespace-nowrap uppercase tracking-tighter border border-white/20 shadow-lg"
                    style={{ backgroundColor: themeColor, fontFamily: '"Times New Roman", Times, serif'}}
                  >
                    {labelData[i].title}
                  </div>
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* MDR WORKFLOW CENTER LABEL */}
        <g>
          {/* Vertical Connection Line */}
          <line 
            x1="250" y1="100" 
            x2="250" y2="210" 
            stroke="#ffffff" 
            strokeWidth="1.5" 
            strokeDasharray="4 2" 
            className="opacity-40"
          />
         
          <foreignObject x="190" y="210" width="120" height="30" className="overflow-visible">
            <div className="flex items-start justify-center w-full">
              {/* py-0.5 and leading-none removes all excess vertical air */}
              <div className="relative px-2.5 py-0.5 backdrop-blur-md border border-white/10 rounded-sm shadow-xl flex items-center">
                
                {/* Thinner accent bar */}
                <div className="absolute left-0 top-1 bottom-1 w-[1.5px] bg-cyan-400" />
                
                <span className="text-white text-[12px] font-bold uppercase tracking-[0.15em] leading-none whitespace-nowrap">
                  SECUWALL MDR <span className="text-cyan-400">Workflow</span>
                </span>
                
                {/* Subtle glow highlight */}
                <div className="absolute -bottom-[1px] left-2 right-2 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
              </div>
            </div>
          </foreignObject>

        </g>

        {/* CENTER HUB */}
        <g filter="url(#neon-glow)">
          <circle cx="250" cy="100" r="5" fill="#ffffff" opacity="0.9" />
          <circle cx="250" cy="100" r="2" fill="#1e293b" />
        </g>
      </svg>
    </div>
    </div>
  );
};

export default CyberInfinityAnimation;